import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Shield, Brain, Database, Eye, Bot, TrendingUp, ArrowRight, CheckCircle } from 'lucide-react';
import { Breadcrumb } from '../../components/Breadcrumb';
import { Schema } from '../../components/Schema';
import { useBanner } from '../../contexts/BannerContext';

export const HealthcareAIPage: React.FC = () => {
  const { isBannerVisible, bannerHeight } = useBanner();

  const healthcareServices = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Cognitive Care EHR",
      description: "Revolutionary Electronic Health Records system launching March 2026. Scalable from solo practitioners to hospital systems.",
      features: ["HIPAA Compliant", "Patient Portals", "Provider Workflows", "Intelligent Scheduling"]
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Clinical Decision Support",
      description: "AI-powered diagnostic assistance and treatment recommendations based on patient data and medical literature.",
      features: ["Diagnostic AI", "Treatment Planning", "Drug Interaction Alerts", "Evidence-Based Medicine"]
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Medical Data Analytics",
      description: "Advanced analytics for patient outcomes, operational efficiency, and population health management.",
      features: ["Predictive Analytics", "Population Health", "Quality Metrics", "Cost Analysis"]
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: "Healthcare Automation",
      description: "Streamline administrative tasks, appointment scheduling, and patient communication workflows.",
      features: ["Automated Scheduling", "Patient Communications", "Insurance Processing", "Billing Automation"]
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Medical Imaging AI",
      description: "Computer vision solutions for radiology, pathology, and diagnostic imaging analysis.",
      features: ["Radiology AI", "Pathology Analysis", "Image Enhancement", "Anomaly Detection"]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Healthcare Cybersecurity",
      description: "Comprehensive security solutions protecting patient data and healthcare infrastructure.",
      features: ["HIPAA Compliance", "Threat Detection", "Data Encryption", "Access Controls"]
    }
  ];

  const benefits = [
    "Support improved patient outcomes with AI-driven insights*",
    "Potential to reduce administrative burden*",
    "Designed to enhance diagnostic support and workflow speed*",
    "Streamline workflows with cost optimization goals*",
    "Built with HIPAA compliance and data security standards",
    "Scalable architecture from solo practices to hospital systems"
  ];

  const useCases = [
    {
      title: "Solo Practitioners",
      description: "Streamline practice management with affordable AI tools designed for independent healthcare providers.",
      solutions: ["Basic EHR", "Patient Scheduling", "Billing Automation", "Telemedicine Integration"]
    },
    {
      title: "Clinics & Group Practices",
      description: "Coordinate care across multiple providers with integrated AI solutions for medium-sized practices.",
      solutions: ["Multi-Provider EHR", "Resource Optimization", "Quality Reporting", "Patient Engagement"]
    },
    {
      title: "Hospitals & Health Systems",
      description: "Enterprise-grade AI solutions for large healthcare organizations and hospital networks.",
      solutions: ["Enterprise EHR", "Population Health", "Clinical Decision Support", "Operational Analytics"]
    },
    {
      title: "Specialty Practices",
      description: "Specialized AI solutions tailored to specific medical specialties and their unique workflows.",
      solutions: ["Specialty Templates", "Diagnostic Tools", "Treatment Protocols", "Outcome Tracking"]
    }
  ];

  return (
    <>
      <Schema 
        type="service" 
        data={{
          name: "Healthcare AI Solutions",
          description: "Comprehensive AI solutions for healthcare providers including EHR systems, clinical decision support, and medical automation",
          serviceType: "Healthcare Technology",
          offerDescription: "AI-powered healthcare solutions from solo practitioners to hospital systems"
        }} 
      />
      <Breadcrumb />
      <main className="pb-20">
        {/* Hero Section */}
        <section 
          className="px-4 sm:px-6 lg:px-8 mb-20"
          style={{ 
            paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px'
          }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent tracking-tight">
                Transform Healthcare with AI
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
                Revolutionize patient care and operational efficiency with comprehensive AI solutions designed specifically for healthcare providers of all sizes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Get Healthcare AI Consultation
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </Link>
                <Link
                  to="/products/cognitive-care"
                  className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
                >
                  Learn About Cognitive Care EHR
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="px-4 sm:px-6 lg:px-8 mb-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Why Healthcare Providers Choose Our AI Solutions
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3 p-6 bg-white/80 dark:bg-white/5 rounded-xl border border-gray-200 dark:border-white/10">
                  <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-700 dark:text-gray-300">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="px-4 sm:px-6 lg:px-8 mb-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Comprehensive Healthcare AI Services
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                From electronic health records to clinical decision support, we provide end-to-end AI solutions for modern healthcare.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {healthcareServices.map((service, index) => (
                <div key={index} className="group p-8 bg-white/80 dark:bg-white/5 rounded-3xl border border-gray-200 dark:border-white/10 hover:shadow-xl hover:scale-105 transition-all duration-300">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mb-6 text-white group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="px-4 sm:px-6 lg:px-8 mb-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Solutions for Every Healthcare Provider
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                Scalable AI solutions designed to meet the unique needs of different healthcare organizations.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div key={index} className="p-8 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/10 dark:to-transparent rounded-3xl border border-blue-200 dark:border-blue-500/20">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    {useCase.description}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {useCase.solutions.map((solution, sIndex) => (
                      <div key={sIndex} className="flex items-center text-sm text-blue-700 dark:text-blue-300">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {solution}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Ready to Transform Your Healthcare Practice?
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                Join healthcare providers across Michigan, Ohio, Texas, Illinois, and Indiana who are already using AI to improve patient care and operational efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
                >
                  Schedule Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <button
                  onClick={() => window.open("https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled", "_blank")}
                  className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
                >
                  Book Demo Call
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Medical Disclaimer */}
        <section className="px-4 sm:px-6 lg:px-8 pb-12 pt-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 text-sm text-gray-600 dark:text-gray-400">
              <p className="mb-2">
                <strong>*Medical & Legal Disclaimer:</strong> Our healthcare AI solutions are designed to support healthcare professionals and are not intended to replace professional medical judgment or diagnosis. All AI-powered clinical decision support tools must be used in conjunction with healthcare professional oversight. Results shown are based on individual implementations and may not be representative of typical outcomes. HIPAA compliance features are designed to meet regulatory requirements but implementation success depends on proper configuration and user adherence to protocols.
              </p>
              <p className="mb-2">
                <strong>Cognitive Care EHR:</strong> Expected launch March 2026 is subject to development milestones, regulatory considerations, and market conditions. Features and timelines may change based on ongoing development and compliance requirements.
              </p>
              <p>
                This content is for informational purposes only and does not constitute medical or professional advice. Healthcare providers should evaluate all AI tools in the context of their specific practice requirements and regulatory obligations.
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};